/* eslint-disable react/jsx-key */
/* eslint-disable no-nested-ternary */
/* eslint-disable react/prop-types */
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTable, useSortBy, useFilters } from 'react-table';
// import { green, red, yellow } from '../../styles/standard-colors.js';

const STATUS_BG = {
    ACTIVE: '#22c55e', // green
    AVAILABLE: '#eab308', // yellow
    WAITING: '#007bff', // blue
    ERROR: '#ef4444' // red
};

const formatDate = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    return d.toLocaleDateString('en-US', { timeZone: 'UTC' });
};

/**
 * TsPlusLicensesTable component for displaying a table of TSplus licenses.
 *
 * @component
 * @example
 * // Example usage:
 * <TsPlusLicensesTable licenses={licenseList} />
 *
 * @param {Object} props - The component props.
 * @param {Array} props.licenses - The array of license objects to be displayed in the table (required).
 *
 * @returns {JSX.Element} - The rendered TsPlusLicensesTable component.
 */
const TsPlusLicensesTable = ({ licenses, onDeleteLicense }) => {
    const navigate = useNavigate();

    // Default text filter
    const DefaultColumnFilter = ({ column: { filterValue, setFilter } }) => (
        <input
            value={filterValue || ''}
            onChange={(e) => setFilter(e.target.value || undefined)}
            placeholder="Search..."
            style={{ width: '100%' }}
        />
    );

    const ExactNumberFilter = ({ column: { filterValue, setFilter } }) => (
        <input
            type="number"
            value={filterValue || ''}
            onChange={(e) => setFilter(e.target.value || undefined)}
            placeholder="Exact number"
            style={{ width: '100%' }}
        />
    );

    const StringMatchDateFilter = ({ column: { filterValue, setFilter } }) => (
        <input
            type="date"
            value={filterValue || ''}
            onChange={(e) => setFilter(e.target.value || undefined)}
            placeholder="Enter date"
            style={{ width: '100%' }}
        />
    );

    const ManualAutoFilter = ({ column: { filterValue, setFilter } }) => (
        <select
            value={filterValue || ''}
            onChange={(e) => setFilter(e.target.value || undefined)}
            style={{ width: '100%' }}>
            <option value="">All</option>
            <option value="MANUAL">Manual</option>
            <option value="AUTO">Auto</option>
        </select>
    );

    const StatusFilter = ({ column: { filterValue, setFilter } }) => (
        <select
            value={filterValue || ''}
            onChange={(e) => setFilter(e.target.value || undefined)}
            style={{ width: '100%' }}>
            <option value="">All</option>
            <option value="ACTIVE">ACTIVE</option>
            <option value="AVAILABLE">AVAILABLE</option>
            <option value="WAITING">WAITING</option>
            <option value="ERROR">ERROR</option>
        </select>
    );

    const columns = useMemo(
        () => [
            {
                Header: 'Status',
                accessor: 'status',
                Filter: StatusFilter,
                filter: 'equals',
                Cell: ({ value }) => {
                    const bg = STATUS_BG[value] || '#9ca3af';
                    const fg = value === 'ERROR' ? '#fff' : '#111827';
                    return (
                        <div
                            style={{
                                backgroundColor: bg,
                                color: fg,
                                padding: '5px 10px',
                                textAlign: 'center',
                                borderRadius: 9999,
                                fontWeight: 700
                            }}>
                            {value || 'UNKNOWN'}
                        </div>
                    );
                }
            },
            {
                Header: 'Office',
                accessor: (row) =>
                    // prefer included office (from computer) -> fallback to officeName
                    row?.computer?.office?.officeSysName ||
                    row?.officeName ||
                    '',
                id: 'office',
                Filter: DefaultColumnFilter,
                Cell: ({ value, row }) => {
                    const officeId =
                        row.original?.computer?.office?._id ||
                        row.original?.officeId ||
                        null;
                    const officeName = value || 'N/A';
                    if (!officeName) return 'N/A';
                    // manual or auto without ID - plain text
                    if (!officeId) return officeName;
                    return <Link to={`/office/${officeId}`}>{officeName}</Link>;
                }
            },
            {
                Header: 'Computer',
                accessor: (row) => row?.computer?.computerSysName || '',
                id: 'computer.computerSysName',
                Filter: DefaultColumnFilter,
                Cell: ({ value }) => value || '-'
            },
            {
                Header: 'Tech',
                accessor: 'tech',
                Filter: DefaultColumnFilter,
                Cell: ({ value }) => value || '-'
            },
            {
                Header: 'License',
                accessor: 'license',
                Filter: DefaultColumnFilter,
                Cell: ({ value }) => value || '-'
            },
            {
                Header: '# of Users',
                accessor: 'numOfUsers',
                Filter: ExactNumberFilter,
                Cell: ({ value }) =>
                    value !== null && value !== undefined ? value : '—'
            },
            // NEW dates
            {
                Header: 'Create Date',
                accessor: (row) => row.createDate || row.startDate || null,
                id: 'createDate',
                Cell: ({ value }) => formatDate(value),
                Filter: StringMatchDateFilter,
                filter: 'stringMatchDate'
            },
            {
                Header: 'Rehost Date',
                accessor: 'rehostDate',
                id: 'rehostDate',
                Cell: ({ value }) => formatDate(value),
                Filter: StringMatchDateFilter,
                filter: 'stringMatchDate'
            },
            // Type (manual vs auto)
            {
                Header: 'Type',
                accessor: 'type',
                id: 'type',
                Filter: ManualAutoFilter,
                filter: 'equals',
                Cell: ({ value }) => (
                    <span
                        style={{
                            padding: '4px 12px',
                            borderRadius: 20,
                            fontSize: '12px',
                            fontWeight: 700,
                            color: '#fff',
                            backgroundColor:
                                value === 'MANUAL' ? '#f59e0b' : '#22c55e'
                        }}>
                        {value}
                    </span>
                )
            },
            {
                Header: 'Actions',
                id: 'actions',
                Cell: ({ row }) => {
                    const lic = row.original;
                    const isManual = lic.type === 'MANUAL';
                    const isAssigned = lic.status === 'ACTIVE';

                    return (
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <button
                                style={{ cursor: 'pointer' }}
                                onClick={() =>
                                    navigate(
                                        isManual
                                            ? '/addManualLicense'
                                            : '/editLicense',
                                        { state: { objectToUpdate: lic } }
                                    )
                                }>
                                Edit
                            </button>
                            <button
                                style={{
                                    color: isAssigned ? '#9ca3af' : 'red',
                                    cursor: isAssigned
                                        ? 'not-allowed'
                                        : 'pointer'
                                }}
                                disabled={isAssigned}
                                title={
                                    isAssigned
                                        ? 'Assigned licenses cannot be deleted'
                                        : 'Delete'
                                }
                                onClick={() => {
                                    if (
                                        !isAssigned &&
                                        // eslint-disable-next-line no-alert
                                        window.confirm(
                                            'Are you sure you want to delete this license?'
                                        )
                                    ) {
                                        onDeleteLicense(lic._id);
                                    }
                                }}>
                                Delete
                            </button>
                        </div>
                    );
                }
            }
        ],
        [navigate, onDeleteLicense]
    );

    const defaultColumn = useMemo(
        () => ({
            Filter: DefaultColumnFilter
        }),
        []
    );

    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
        useTable(
            {
                columns,
                data: licenses,
                defaultColumn
            },
            useFilters,
            useSortBy
        );

    // Calculate the total number of licenses based on the filtered rows
    const totalLicenses = rows.length;
    const manualCount = rows.filter((r) => r.original.type === 'MANUAL').length;
    const autoCount = rows.filter((r) => r.original.type === 'AUTO').length;

    return (
        <>
            <div
                style={{
                    width: '97%',
                    display: 'flex',
                    justifyContent: 'left',
                    alignItems: 'center',
                    gap: '20px',
                    position: 'sticky',
                    zIndex: 1000,
                    top: 78,
                    backgroundColor: 'white',
                    padding: '10px 0'
                }}>
                <div style={{ fontWeight: 'bold' }}>
                    Total Licenses: {totalLicenses}
                </div>
                <div style={{ color: '#3498db', fontWeight: 'bold' }}>
                    Manual: {manualCount}
                </div>
                <div style={{ color: '#2ecc71', fontWeight: 'bold' }}>
                    Auto: {autoCount}
                </div>
            </div>
            <div className="table-container">
                <table {...getTableProps()} className="styled-table">
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1000 }}>
                        {headerGroups.map((headerGroup) => (
                            <tr {...headerGroup.getHeaderGroupProps()}>
                                {headerGroup.headers.map((column) => (
                                    <th
                                        {...column.getHeaderProps(
                                            column.getSortByToggleProps()
                                        )}>
                                        {column.render('Header')}
                                        <span>
                                            {column.isSorted
                                                ? column.isSortedDesc
                                                    ? ' 🔽'
                                                    : ' 🔼'
                                                : ''}
                                        </span>
                                        <br />
                                        <br />
                                        <div>
                                            {column.canFilter
                                                ? column.render('Filter')
                                                : null}
                                        </div>
                                    </th>
                                ))}
                            </tr>
                        ))}
                    </thead>
                    <tbody {...getTableBodyProps()}>
                        {rows.map((row) => {
                            prepareRow(row);
                            return (
                                <tr {...row.getRowProps()}>
                                    {row.cells.map((cell) => (
                                        <td {...cell.getCellProps()}>
                                            {cell.render('Cell')}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </>
    );
};

TsPlusLicensesTable.propTypes = {
    licenses: PropTypes.arrayOf(PropTypes.object).isRequired,
    onDeleteLicense: PropTypes.func.isRequired
};

export default TsPlusLicensesTable;
